import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Radio, Eye, EyeOff, Save, X, ExternalLink, VideoOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

export default function AdminAnnouncements() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [announcement, setAnnouncement] = useState({
    enabled: false,
    message: '',
    is_live: false,
    live_title: '',
    stream_url: ''
  });
  const [editMessage, setEditMessage] = useState('');
  const [liveTitle, setLiveTitle] = useState('');
  const [streamUrl, setStreamUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'overseer') {
      navigate('/dashboard');
      return;
    }
    fetchAnnouncement();
  }, [user, navigate]);

  const fetchAnnouncement = async () => {
    try {
      const response = await apiClient.get('/announcement');
      setAnnouncement(response.data);
      setEditMessage(response.data.message || '');
      setLiveTitle(response.data.live_title || '');
      setStreamUrl(response.data.stream_url || '');
    } catch (error) {
      console.error('Failed to fetch announcement');
    } finally {
      setLoading(false);
    }
  };

  const updateAnnouncement = async (enabled, message) => {
    try {
      await apiClient.put(`/admin/announcement?enabled=${enabled}&message=${encodeURIComponent(message)}`);
      toast.success('Announcement updated!');
      fetchAnnouncement();
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const startLiveStream = async () => {
    if (!liveTitle) {
      toast.error('Please enter a live stream title');
      return;
    }

    try {
      await apiClient.put(`/admin/announcement/live?is_live=true&live_title=${encodeURIComponent(liveTitle)}&stream_url=${encodeURIComponent(streamUrl)}`);
      toast.success('Live stream started! All visitors can now see it.');
      fetchAnnouncement();
    } catch (error) {
      toast.error('Failed to start stream');
    }
  };

  const stopLiveStream = async () => {
    try {
      await apiClient.put('/admin/announcement/live?is_live=false&live_title=&stream_url=');
      toast.success('Live stream ended');
      setLiveTitle('');
      setStreamUrl('');
      fetchAnnouncement();
    } catch (error) {
      toast.error('Failed to stop stream');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
            <Megaphone className="text-violet-400" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Announcements</h1>
            <p className="text-white/60">Manage site-wide announcements and live streams</p>
          </div>
        </div>

        {/* Daily Announcement */}
        <Card className="glass-default border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="text-violet-400" size={20} />
              Daily Announcement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => updateAnnouncement(!announcement.enabled, editMessage)}
                className={announcement.enabled 
                  ? "bg-green-600 hover:bg-green-500" 
                  : "bg-gray-600 hover:bg-gray-500"
                }
              >
                {announcement.enabled ? <Eye size={16} className="mr-2" /> : <EyeOff size={16} className="mr-2" />}
                {announcement.enabled ? 'Visible' : 'Hidden'}
              </Button>
              <span className={`text-sm ${announcement.enabled ? 'text-green-400' : 'text-white/40'}`}>
                {announcement.enabled ? 'Announcement is visible to all visitors' : 'Announcement is hidden'}
              </span>
            </div>

            <div>
              <label className="text-sm text-white/60 mb-2 block">Announcement Message</label>
              <div className="flex gap-3">
                <Input
                  value={editMessage}
                  onChange={(e) => setEditMessage(e.target.value)}
                  placeholder="Enter your daily announcement message..."
                  className="bg-white/5 border-white/10 focus:border-violet-500 text-white flex-1"
                />
                <Button
                  onClick={() => updateAnnouncement(announcement.enabled, editMessage)}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  <Save size={16} className="mr-2" />
                  Save
                </Button>
              </div>
            </div>

            {announcement.enabled && announcement.message && (
              <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-lg p-3">
                <p className="text-sm text-white/80 mb-1">Preview:</p>
                <p className="text-white font-medium">{announcement.message}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Live Stream */}
        <Card className="glass-default border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Radio className={`${announcement.is_live ? 'text-red-500 animate-pulse' : 'text-red-400'}`} size={20} />
              Go Live
              {announcement.is_live && (
                <span className="ml-2 px-2 py-0.5 bg-red-500 rounded-full text-xs animate-pulse">
                  LIVE NOW
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!announcement.is_live ? (
              <>
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Live Stream Title *</label>
                  <Input
                    value={liveTitle}
                    onChange={(e) => setLiveTitle(e.target.value)}
                    placeholder="e.g., Big Announcement! New Features Coming..."
                    className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                  />
                </div>

                <div>
                  <label className="text-sm text-white/60 mb-2 block">
                    Stream URL (YouTube Live, Twitch, etc.)
                  </label>
                  <Input
                    value={streamUrl}
                    onChange={(e) => setStreamUrl(e.target.value)}
                    placeholder="https://youtube.com/live/... or https://twitch.tv/..."
                    className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                  />
                  <p className="text-xs text-white/40 mt-2">
                    Paste your YouTube Live or Twitch stream URL. All visitors will see the embedded stream.
                  </p>
                </div>

                <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
                  <h4 className="font-semibold text-violet-300 mb-2">How to Go Live:</h4>
                  <ol className="text-sm text-white/70 space-y-1 list-decimal list-inside">
                    <li>Start a live stream on YouTube or Twitch</li>
                    <li>Copy your stream URL</li>
                    <li>Paste it above and click Start Live</li>
                    <li>All visitors will see your stream on the homepage!</li>
                  </ol>
                </div>

                <Button
                  onClick={startLiveStream}
                  disabled={!liveTitle}
                  className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500"
                >
                  <Radio size={18} className="mr-2" />
                  Start Live Stream
                </Button>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Radio className="w-10 h-10 text-red-500 animate-pulse" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">You are LIVE!</h3>
                <p className="text-white/60 mb-4">{announcement.live_title}</p>
                {announcement.stream_url && (
                  <a 
                    href={announcement.stream_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-violet-400 hover:text-violet-300 flex items-center justify-center gap-2 mb-6"
                  >
                    <ExternalLink size={16} />
                    View Stream
                  </a>
                )}
                <Button
                  onClick={stopLiveStream}
                  className="bg-red-600 hover:bg-red-500"
                >
                  <VideoOff size={18} className="mr-2" />
                  End Live Stream
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
